import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Save } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function ReportHeaderPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [lookups, setLookups] = useState({});
  const [foreman, setForeman] = useState("");
  const [weather, setWeather] = useState("");
  const [generalNotes, setGeneralNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, l] = await Promise.all([
          api.get(`/reports/${reportId}`),
          api.get("/lookups"),
        ]);
        setReport(r);
        setLookups(l);
        setForeman(r.foreman || "");
        setWeather(r.weather || "");
        setGeneralNotes(r.general_notes || "");
      } catch (err) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportId]);

  const handleSave = async () => {
    if (!foreman.trim()) {
      toast.error("Supervisor/Foreman name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/reports/${reportId}`, { foreman, weather, general_notes: generalNotes });
      toast.success("Header saved");
      navigate(`/report/${reportId}/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!report) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Report not found</div>;

  const siteName = lookups.sites?.find(s => s.id === report.site_id)?.name || "—";
  const isReadOnly = report.status === "Submitted";

  return (
    <div className="min-h-screen" data-testid="report-header-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-headline text-2xl font-bold tracking-tight">Report Header</h1>
          <Badge variant="outline" className={isReadOnly ? "bg-primary/20 text-primary border-primary/30" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"}>
            {report.status}
          </Badge>
        </div>

        {/* Read-only info */}
        <Card className="border-border bg-card mb-6">
          <CardContent className="py-4 px-5 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Date</p>
              <p className="font-mono text-sm mt-1">{report.report_date}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Site</p>
              <p className="text-sm mt-1">{siteName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift</p>
              <p className="text-sm mt-1">{report.shift}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Shift Start</p>
              <p className="font-mono text-sm mt-1">
                {report.shift_start ? new Date(report.shift_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Editable fields */}
        <Card className="border-border bg-card">
          <CardContent className="pt-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Operator (Supervisor / Foreman / Operator Name) *</Label>
              <Input
                data-testid="foreman-input"
                value={foreman}
                onChange={(e) => setForeman(e.target.value)}
                placeholder="Enter name"
                className="bg-background border-border"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Weather</Label>
              <Select value={weather} onValueChange={setWeather} disabled={isReadOnly}>
                <SelectTrigger data-testid="weather-select" className="bg-background border-border">
                  <SelectValue placeholder="Select weather" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Dry">Dry</SelectItem>
                  <SelectItem value="Wet">Wet</SelectItem>
                  <SelectItem value="Muddy">Muddy</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">General Notes</Label>
              <textarea
                data-testid="general-notes-input"
                value={generalNotes}
                onChange={(e) => setGeneralNotes(e.target.value)}
                placeholder="Any notes for this shift..."
                className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                disabled={isReadOnly}
              />
            </div>

            {!isReadOnly && (
              <Button
                onClick={handleSave}
                data-testid="save-header-button"
                className="w-full font-headline font-semibold h-11"
                disabled={saving}
              >
                <Save className="h-4 w-4 mr-2" strokeWidth={2} />
                {saving ? "Saving..." : "Save & Continue"}
              </Button>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
